import type { InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import { API_URL } from "src/config";
import localStorageManager from "src/utils/localStorageManager";

const authRequestInterceptor = (config: InternalAxiosRequestConfig) => {
    const newConfig = { ...config };

    if (config.headers !== undefined) {
        const token = localStorageManager.getItem("token");

        if (token !== null) {
            newConfig.headers.Authorization = `Bearer ${token}`;
        }
    }
    return config;
};

// レスポンスインターセプター
const responseInterceptor = {
    success: (response: any) => {
        // Unwrap v3 API { data: T } wrapper (but not paginated { data: T[], meta: {...} })
        if (response.data?.data !== undefined && response.data?.meta === undefined) {
            response.data = response.data.data;
        }
        return response;
    },
    error: async (error: any) => {
        const originalRequest = error.config;

        // トークン期限切れ（401エラー）の処理
        // ただし、ログイン失敗エラーは除外
        const isLoginRequest = originalRequest.url?.includes('/login');
        if (error.response?.status === 401 && !originalRequest.isRetry && !isLoginRequest) {
            originalRequest.isRetry = true;

            // トーストメッセージを表示
            try {
                const { toast } = await import("react-toastify");
                toast.error("セッションが期限切れです。再度ログインしてください。");
            } catch (toastError) {
                console.warn("トーストメッセージの表示に失敗しました:", toastError);
            }

            // 認証状態をクリア
            try {
                const { isLogin_atom } = await import("src/features/Auth/auth.atom");
                const { getDefaultStore } = await import("jotai");
                const store = getDefaultStore();
                store.set(isLogin_atom, false);
            } catch (atomError) {
                console.warn("認証状態の更新に失敗しました:", atomError);
            }

            // ローカルストレージをクリアしてログイン画面にリダイレクト
            localStorageManager.clearAll();

            // SWRキャッシュをクリア
            try {
                const { default: clearSWRCache } = await import("src/utils/clearSWRCache");
                clearSWRCache();
            } catch (clearError) {
                console.warn("SWRキャッシュのクリアに失敗しました:", clearError);
            }

            // 少し遅延してからリダイレクト（トーストメッセージが表示されるまで）
            setTimeout(() => {
                window.location.href = "/login";
            }, 1000);

            return Promise.reject(error);
        }

        // 権限エラー（403エラー）の処理
        if (error.response?.status === 403 && !originalRequest.isRetry) {
            try {
                const { toast } = await import("react-toastify");
                toast.error("この操作を行う権限がありません。");
            } catch (toastError) {
                console.warn("トーストメッセージの表示に失敗しました:", toastError);
            }
            return Promise.reject(error);
        }

        // レート制限（429エラー）の処理（リトライ制限付き）
        if (error.response?.status === 429 && !originalRequest.isRetry && !originalRequest.retryCount) {
            originalRequest.isRetry = true;
            originalRequest.retryCount = (originalRequest.retryCount || 0) + 1;

            // 最大3回まででリトライ制限
            if (originalRequest.retryCount > 3) {
                try {
                    const { toast } = await import("react-toastify");
                    toast.error("リクエストが多すぎます。しばらく時間をおいてから再試行してください。");
                } catch (toastError) {
                    console.warn("トーストメッセージの表示に失敗しました:", toastError);
                }
                return Promise.reject(error);
            }

            // Retry-Afterヘッダーがある場合はそれを使用、なければデフォルト値
            const retryAfter = error.response.headers['retry-after'];
            const delay = retryAfter ? parseInt(retryAfter, 10) * 1000 : 3000; // 3秒デフォルト
            const delaySeconds = Math.ceil(delay / 1000);

            // トーストメッセージを表示
            try {
                const { toast } = await import("react-toastify");
                toast.warning(`リクエストが多すぎます。${delaySeconds}秒後に自動で再試行します。（${originalRequest.retryCount}/3回目）`);
            } catch (toastError) {
                console.warn("トーストメッセージの表示に失敗しました:", toastError);
            }

            // 遅延後にリトライ
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve(axios(originalRequest));
                }, delay);
            });
        }

        // サーバーエラー（500系エラー）の処理
        if (error.response?.status >= 500 && error.response?.status < 600) {
            try {
                const { toast } = await import("react-toastify");
                toast.error("サーバーエラーが発生しました。しばらく時間をおいてから再試行してください。");
            } catch (toastError) {
                console.warn("トーストメッセージの表示に失敗しました:", toastError);
            }
            return Promise.reject(error);
        }

        // ネットワークエラーの処理
        if (!error.response && error.code === 'NETWORK_ERROR') {
            try {
                const { toast } = await import("react-toastify");
                toast.error("ネットワークエラーが発生しました。インターネット接続を確認してください。");
            } catch (toastError) {
                console.warn("トーストメッセージの表示に失敗しました:", toastError);
            }
        }

        // タイムアウトエラーの処理
        if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
            try {
                const { toast } = await import("react-toastify");
                toast.error("リクエストがタイムアウトしました。再試行してください。");
            } catch (toastError) {
                console.warn("トーストメッセージの表示に失敗しました:", toastError);
            }
        }

        return Promise.reject(error);
    }
};

export const streetApi = axios.create({
    baseURL: API_URL,
    headers: {
        "Content-Type": "application/json",
    },
    timeout: 15000, // 15秒のタイムアウト
});

export const streetApiToPostFormData = axios.create({
    baseURL: API_URL,
    timeout: 30000, // ファイルアップロード用に30秒のタイムアウト
});

// インターセプターの設定
streetApi.interceptors.request.use(authRequestInterceptor);
streetApiToPostFormData.interceptors.request.use(authRequestInterceptor);

streetApi.interceptors.response.use(
    responseInterceptor.success,
    responseInterceptor.error
);
streetApiToPostFormData.interceptors.response.use(
    responseInterceptor.success,
    responseInterceptor.error
);
